import React, { useMemo, useState } from 'react';
import { Filter, Search, Star } from 'lucide-react';
import type { NivelClase, Recurso } from '../../types';
import { CAPACIDADES, ENTORNOS, FAMILIAS, INTENSIDADES, NIVELES_EJERCICIO } from '../../data/constantes';
import { Chip, Insignia } from '../ui/Etiquetas';
import { Boton } from '../ui/Boton';
import { nivelSuperaClase } from '../../utils/reglas';
interface Props {
  recursos: Recurso[];
  favoritos: string[];
  recientes: string[];
  alternarFavorito: (id: string) => void;
  onAbrirFicha: (recurso: Recurso) => void;
  nivelClase?: NivelClase;
  coleccionNivel?: string[];
  accionSecundaria?: (recurso: Recurso) => void;
  etiquetaAccion?: string;
}
const TODAS = 'Todas';
const TODOS = 'Todos';
export function ExploradorRecursos({
  recursos,
  favoritos,
  recientes,
  alternarFavorito,
  onAbrirFicha,
  nivelClase,
  coleccionNivel,
  accionSecundaria,
  etiquetaAccion = 'Agregar'
}: Props) {
  const [busqueda, setBusqueda] = useState('');
  const [familia, setFamilia] = useState<string>(TODAS);
  const [nivel, setNivel] = useState<string>(TODOS);
  const [entorno, setEntorno] = useState<string>(TODOS);
  const [intensidad, setIntensidad] = useState<string>(TODAS);
  const [capacidad, setCapacidad] = useState<string>(TODAS);
  const [inversion, setInversion] = useState<string>(TODAS);
  const [evaluacion, setEvaluacion] = useState<string>(TODOS);
  const [multinivel, setMultinivel] = useState<string>(TODOS);
  const [chip, setChip] = useState<'ninguno' | 'favoritos' | 'recientes' | 'personalizados'>('ninguno');
  const [verTodosLosNiveles, setVerTodosLosNiveles] = useState(false);
  const [filtrosAbiertos, setFiltrosAbiertos] = useState(false);
  const filtrados = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    let lista = recursos.filter(r => {
      if (q) {
        const texto = `${r.id} ${r.nombre} ${r.alias.join(' ')}`.toLowerCase();
        if (!texto.includes(q)) return false;
      }
      if (familia !== TODAS && r.familia !== familia) return false;
      if (nivel !== TODOS && r.nivelMinimo !== nivel) return false;
      if (entorno !== TODOS && !r.entornos.includes(entorno as never)) return false;
      if (intensidad !== TODAS && r.intensidad !== intensidad) return false;
      if (capacidad !== TODAS && !r.capacidades.includes(capacidad as never)) return false;
      if (inversion === 'Solo inversiones' && r.familia !== 'Inversiones y control') return false;
      if (inversion === 'Sin inversiones' && r.familia === 'Inversiones y control') return false;
      if (evaluacion === 'Sí' && !r.requiereEvaluacionDocente) return false;
      if (evaluacion === 'No' && r.requiereEvaluacionDocente) return false;
      if (multinivel === 'Sí' && !r.aptoMultinivel) return false;
      if (multinivel === 'No' && r.aptoMultinivel) return false;
      if (chip === 'favoritos' && !favoritos.includes(r.id)) return false;
      if (chip === 'recientes' && !recientes.includes(r.id)) return false;
      if (chip === 'personalizados' && !r.personalizado) return false;
      return true;
    });
    if (coleccionNivel && coleccionNivel.length > 0 && !verTodosLosNiveles) {
      lista = lista.filter(r => coleccionNivel.includes(r.id) || r.personalizado);
    }
    if (chip === 'recientes') {
      lista = [...lista].sort((a, b) => recientes.indexOf(a.id) - recientes.indexOf(b.id));
    }
    return lista;
  }, [recursos, busqueda, familia, nivel, entorno, intensidad, capacidad, inversion, evaluacion, multinivel, chip, favoritos, recientes, coleccionNivel, verTodosLosNiveles]);
  return <div className="space-y-4" data-id="element-238" data-mp="20a60oc79otav">
      <div className="flex flex-col gap-3" data-id="element-239" data-mp="20cjvcjy1c3hw">
        <label className="relative block" data-id="element-240" data-mp="1vcnp0e1v7zz6">
          <span className="sr-only" data-id="element-241" data-mp="1vdp9180uzwt1">Buscar por nombre o alias</span>
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-tinta-tenue" aria-hidden="true" data-id="element-242" data-mp="29c6s2sftkl4m" />
          <input type="search" value={busqueda} onChange={e => setBusqueda(e.target.value)} placeholder="Buscar por nombre o alias (por ejemplo, hip key)" className="min-h-[44px] w-full rounded-xl border border-linea bg-white pl-9 pr-3 text-sm text-tinta placeholder:text-tinta-tenue focus:border-vino focus:outline-none focus:ring-2 focus:ring-vino/20" data-id="element-243" data-mp="2nwrlmdla5jb" />
        </label>

        <div className="flex flex-wrap gap-2" data-id="element-244" data-mp="3fvhmzehpryao">
          <Chip activo={chip === 'favoritos'} onClick={() => setChip(chip === 'favoritos' ? 'ninguno' : 'favoritos')} data-id="element-245" data-mp="3ks85e8gzr5z9">
            <Star className="h-4 w-4" aria-hidden="true" data-id="element-246" data-mp="3ptxh7qc1fnoe" />
            Favoritos
          </Chip>
          <Chip activo={chip === 'recientes'} onClick={() => setChip(chip === 'recientes' ? 'ninguno' : 'recientes')} data-id="element-247" data-mp="3tur20au6omd2">
            Recientes
          </Chip>
          <Chip activo={chip === 'personalizados'} onClick={() => setChip(chip === 'personalizados' ? 'ninguno' : 'personalizados')} data-id="element-248" data-mp="191dx58n8uwae">
            Personalizados
          </Chip>
          <Chip activo={filtrosAbiertos} onClick={() => setFiltrosAbiertos(v => !v)} data-id="element-249" data-mp="2plqzalp4h6js">
            <Filter className="h-4 w-4" aria-hidden="true" data-id="element-250" data-mp="2kr3cqsdoim7u" />
            Filtros
          </Chip>
          {coleccionNivel && coleccionNivel.length > 0 && <Chip activo={verTodosLosNiveles} onClick={() => setVerTodosLosNiveles(v => !v)} data-id="element-251" data-mp="2av07hdnj67go">
              Ver todos los niveles
            </Chip>}
        </div>

        {filtrosAbiertos && <div className="grid grid-cols-1 gap-3 rounded-2xl border border-linea bg-white p-4 sm:grid-cols-2 lg:grid-cols-4" data-id="element-252" data-mp="21v3dcwidgge8">
            <Selector etiqueta="Familia" valor={familia} onChange={setFamilia} opciones={[TODAS, ...FAMILIAS]} data-id="element-253" data-mp="1wwtjqqcapnuq" />
            <Selector etiqueta="Nivel mínimo" valor={nivel} onChange={setNivel} opciones={[TODOS, ...NIVELES_EJERCICIO]} data-id="element-254" data-mp="m9pplpk4yw" />
            <Selector etiqueta="Entorno" valor={entorno} onChange={setEntorno} opciones={[TODOS, ...ENTORNOS]} data-id="element-255" data-mp="e8h7jf6oscis" />
            <Selector etiqueta="Intensidad" valor={intensidad} onChange={setIntensidad} opciones={[TODAS, ...INTENSIDADES]} data-id="element-256" data-mp="izh58mnmqymc" />
            <Selector etiqueta="Capacidad" valor={capacidad} onChange={setCapacidad} opciones={[TODAS, ...CAPACIDADES]} data-id="element-257" data-mp="58u46nuecwn3" />
            <Selector etiqueta="Inversión" valor={inversion} onChange={setInversion} opciones={[TODAS, 'Solo inversiones', 'Sin inversiones']} data-id="element-258" data-mp="a6vm35g36pwz" />
            <Selector etiqueta="Requiere evaluación docente" valor={evaluacion} onChange={setEvaluacion} opciones={[TODOS, 'Sí', 'No']} data-id="element-259" data-mp="11vp9bn4wp3g2" />
            <Selector etiqueta="Apto para multinivel" valor={multinivel} onChange={setMultinivel} opciones={[TODOS, 'Sí', 'No']} data-id="element-260" data-mp="syy8yyjbn2fo" />
          </div>}
      </div>

      <p className="text-sm text-tinta-tenue" aria-live="polite" data-id="element-261" data-mp="3631xn8hhyl7u">
        {filtrados.length} {filtrados.length === 1 ? 'recurso' : 'recursos'}
      </p>

      {filtrados.length === 0 ? <div className="rounded-2xl border border-linea bg-white p-6 text-center" data-id="element-262" data-mp="3f51fg30c5tbu">
          <p className="text-sm font-semibold text-tinta-titulo" data-id="element-263" data-mp="3a5eypjlatuya">Sin resultados</p>
          <p className="mt-1 text-sm text-tinta-sec" data-id="element-264" data-mp="3a3lu09u8xksx">
            Probá con otro término, quitá filtros o activá “Ver todos los niveles”.
          </p>
        </div> : <ul className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3" data-id="element-265" data-mp="2ig3jihgunpd3">
          {filtrados.map(r => {
        const superior = nivelClase ? nivelSuperaClase(r, nivelClase) : false;
        return <li key={r.id} className="flex flex-col rounded-2xl border border-linea bg-white p-4" data-id="element-266" data-mp="2rd42u9p2ax9o">
                <div className="flex items-start justify-between gap-2" data-id="element-267" data-mp="1yp89neypbnhn">
                  <div data-id="element-268" data-mp="1e4pq7jcrc9hr">
                    <p className="text-xs font-bold text-vino" data-id="element-269" data-mp="1e5hmyxi3cd9m">{r.id}</p>
                    <h3 className="text-sm font-bold text-tinta-titulo" data-id="element-270" data-mp="1i5ronxyuohio">{r.nombre}</h3>
                  </div>
                  <button type="button" onClick={() => alternarFavorito(r.id)} aria-pressed={favoritos.includes(r.id)} aria-label={favoritos.includes(r.id) ? `Quitar ${r.nombre} de favoritos` : `Marcar ${r.nombre} como favorito`} className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl hover:bg-oro/10" data-id="element-271" data-mp="1n4l5o0dciqnl">
                    <Star className={`h-5 w-5 ${favoritos.includes(r.id) ? 'fill-[#FACC15] text-[#EAB308]' : 'text-tinta-tenue'}`} aria-hidden="true" data-id="element-272" data-mp="2au74untpx5vl" />
                  </button>
                </div>

                <div className="mt-2 flex flex-wrap gap-1.5" data-id="element-273" data-mp="96uj1kdah271">
                  <Insignia tono="vino" data-id="element-274" data-mp="466oiopuq382">{r.nivelMinimo}</Insignia>
                  <Insignia data-id="element-275" data-mp="3vcrgcanqdf65">{r.duracionMin}–{r.duracionMax} min</Insignia>
                  <Insignia tono="azul" data-id="element-276" data-mp="3veu1aga10i40">{r.entornos.join(' · ')}</Insignia>
                  {r.personalizado && <Insignia tono="violeta" data-id="element-277" data-mp="3qfhetwn2hd1y">Personalizado</Insignia>}
                </div>

                <p className="mt-2 flex-1 text-sm text-tinta-sec" data-id="element-278" data-mp="rvhhhk0ymtbn">{r.objetivo}</p>

                {(r.requiereEvaluacionDocente || superior || r.dinamico) && <div className="mt-2 space-y-1" data-id="element-279" data-mp="i5fwj13wlr0y">
                    {r.requiereEvaluacionDocente && <p className="text-xs font-semibold text-oro-oscuro" data-id="element-280" data-mp="d63agikuwnzf">
                        Requiere evaluación docente
                      </p>}
                    {r.dinamico && <p className="text-xs font-semibold text-alerta" data-id="element-281" data-mp="15v0i7rrfz2dx">Dinámico o caída</p>}
                    {superior && <p className="text-xs font-semibold text-alerta" data-id="element-282" data-mp="39h44ajys5axe">
                        Por encima del nivel elegido
                      </p>}
                  </div>}

                <div className="mt-3 flex flex-wrap gap-2" data-id="element-283" data-mp="3ih9a4pobs19g">
                  <Boton variante="contorno" tamano="sm" onClick={() => onAbrirFicha(r)} data-id="element-284" data-mp="3ijvg4kvki8pk">
                    Ver ficha
                  </Boton>
                  {accionSecundaria && <Boton tamano="sm" onClick={() => accionSecundaria(r)} data-id="element-285" data-mp="3sfq4lwmh2y09">
                      {etiquetaAccion}
                    </Boton>}
                </div>
              </li>;
      })}
        </ul>}
    </div>;
}
function Selector({
  etiqueta,
  valor,
  onChange,
  opciones
}: {
  etiqueta: string;
  valor: string;
  onChange: (v: string) => void;
  opciones: string[];
}) {
  return <label className="block" data-id="element-286" data-mp="ymvm5vli7f6w">
      <span className="mb-1 block text-xs font-semibold text-tinta-titulo" data-id="element-287" data-mp="ymltu1czkvy1">{etiqueta}</span>
      <select value={valor} onChange={e => onChange(e.target.value)} className="min-h-[44px] w-full rounded-xl border border-linea bg-white px-3 text-sm text-tinta focus:border-vino focus:outline-none focus:ring-2 focus:ring-vino/20" data-id="element-288" data-mp="12oqon3f67ym9">
        {opciones.map(o => <option key={o} value={o} data-id="element-289" data-mp="eymjgrmc3qt5">
            {o}
          </option>)}
      </select>
    </label>;
}